import { useCallback, useMemo, useState } from "react";

import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";

import {
  COLLABORATOR_FUND_SUBJECT_TYPES,
  getFundVehicleTabFromPermission,
} from "../components/collaboratorsV2Constants";

export const getFundRoleCount = (collaborator: CollaboratorDetails): number => {
  const fundPermissions =
    collaborator.permissions?.filter(
      (permission) =>
        permission.roleId &&
        permission.roleSubjectType &&
        COLLABORATOR_FUND_SUBJECT_TYPES.includes(permission.roleSubjectType),
    ) ?? [];

  const uniqueRowKeys = new Set(
    fundPermissions.map(
      (permission) =>
        `${permission.roleId}:${getFundVehicleTabFromPermission(permission)}`,
    ),
  );

  return uniqueRowKeys.size;
};

export const useManageRolesSelection = ({
  collaborators,
}: {
  collaborators: CollaboratorDetails[];
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set(),
  );

  const selectableIds = useMemo(
    () =>
      collaborators
        .filter((collaborator) => getFundRoleCount(collaborator) > 0)
        .map((collaborator) => collaborator.id),
    [collaborators],
  );

  const isAllSelectableSelected = useMemo(
    () =>
      selectableIds.length > 0 &&
      selectableIds.every((id) => selectedUserIds.has(id)),
    [selectableIds, selectedUserIds],
  );

  const isPartiallySelected = useMemo(
    () =>
      !isAllSelectableSelected &&
      selectableIds.some((id) => selectedUserIds.has(id)),
    [isAllSelectableSelected, selectableIds, selectedUserIds],
  );

  const toggleUser = useCallback((userId: string) => {
    setSelectedUserIds((previous) => {
      const next = new Set(previous);

      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }

      return next;
    });
  }, []);

  const toggleAllSelectable = useCallback(() => {
    setSelectedUserIds((previous) => {
      const allSelected =
        selectableIds.length > 0 &&
        selectableIds.every((id) => previous.has(id));

      return allSelected ? new Set() : new Set(selectableIds);
    });
  }, [selectableIds]);

  const clearSelection = useCallback(() => {
    setSelectedUserIds(new Set());
  }, []);

  return {
    selectedUserIds,
    toggleUser,
    toggleAllSelectable,
    clearSelection,
    isAllSelectableSelected,
    isPartiallySelected,
    selectableCount: selectableIds.length,
  };
};
